// Portal Academy — Clair Premium streaming experience (Apple TV+ style)
// for paid subscribers. Lists Bunny Stream library 677110 and plays via
// the gated /api/bunny/embed/:guid endpoint.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Play,
  ArrowRight,
  X,
  Loader2,
  Tv,
  Lock,
  ChevronLeft,
  ChevronRight,
  Search,
  Clock,
  Sparkles,
} from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { useAuth } from "@/lib/auth";
import { paymentsApi } from "@/lib/paymentsApi";
import { apiFetch } from "@/lib/api";

const AUTH_KEY = "geovinum.auth.v1";

function authHeaders() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const t = parsed?.token;
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch {
    return {};
  }
}

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return "";
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem}min` : `${h}h`;
};

const PortalAcademyPage = () => {
  const navigate = useNavigate();
  const { user, ready } = useAuth();
  const [sub, setSub] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!ready) return;
    if (!user) {
      navigate("/login?next=/portal/academy", { replace: true });
      return;
    }
    (async () => {
      try {
        const s = await paymentsApi.mySubscription();
        const active =
          s.subscription && ["active", "trialing"].includes(s.subscription.status);
        setSub(s.subscription);
        if (!active) {
          setLoading(false);
          return;
        }
        const { ok, data } = await apiFetch(`/api/bunny/videos`, { headers: authHeaders() });
        if (ok) setVideos(data.items || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [ready, user, navigate]);

  const play = async (video) => {
    const { ok, data, error: err } = await apiFetch(`/api/bunny/embed/${video.guid}`, {
      headers: authHeaders(),
    });
    if (!ok) {
      window.alert(err || "Impossible de charger la vidéo");
      return;
    }
    setPlaying({ ...video, iframe_url: data.iframe_url });
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return videos;
    return videos.filter((v) => (v.title || "").toLowerCase().includes(q));
  }, [videos, query]);

  if (loading) {
    return (
      <Layout testId="page-portal-academy-loading">
        <div className="min-h-[70vh] flex items-center justify-center bg-[#fafaf7]">
          <Loader2
            size={28}
            className="animate-spin text-[color:var(--gv-blue)]"
          />
        </div>
      </Layout>
    );
  }

  // Gating
  const isActive = sub && ["active", "trialing"].includes(sub.status);
  if (!isActive) {
    return (
      <Layout testId="page-portal-academy-locked">
        <SEO
          title="Academy — geoVINUM"
          description="Le streaming des terroirs et des grands vins."
        />
        <section className="bg-gradient-to-b from-[#fafaf7] via-white to-[#f4ecdf] py-24 md:py-32 min-h-[80vh] flex items-center">
          <div className="max-w-2xl mx-auto px-6 lg:px-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white border border-[color:var(--gv-line)] shadow-sm mb-6">
              <Lock size={26} className="text-[color:var(--gv-blue)]" />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-4 text-[color:var(--gv-ink)]">
              Abonnez-vous pour accéder à l&apos;Academy
            </h1>
            <p className="text-[color:var(--gv-ink)]/70 mb-8 leading-relaxed text-base md:text-lg">
              Accès illimité à toutes les séries et masterclasses geoVINUM
              Academy. <strong>19 € / mois</strong> ou{" "}
              <strong>190 € / an HT</strong>.
            </p>
            <Link
              to="/academy#pricing"
              className="btn-primary"
              data-testid="portal-academy-subscribe"
            >
              Voir les formules <ArrowRight size={14} />
            </Link>
          </div>
        </section>
      </Layout>
    );
  }

  const featured = filtered[0] || videos[0];
  const continueWatching = filtered.slice(0, 6);
  const allSeries = filtered;
  const newDrops = [...filtered]
    .sort((a, b) => new Date(b.created || 0) - new Date(a.created || 0))
    .slice(0, 8);

  return (
    <Layout testId="page-portal-academy">
      <SEO
        title="Academy — geoVINUM"
        description="Votre bibliothèque streaming des terroirs."
      />

      {/* HERO — Apple TV+ inspired */}
      <section className="relative bg-[#fafaf7] overflow-hidden">
        {/* Backdrop image */}
        <div className="absolute inset-0">
          {featured?.thumbnail ? (
            <img
              src={featured.thumbnail}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            <img
              src="/images/educativos.jpg"
              alt=""
              className="w-full h-full object-cover"
            />
          )}
          {/* Soft white overlay for clair premium feel */}
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#fafaf7] via-transparent to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-28 md:pt-36 pb-16 md:pb-24">
          <Link
            to="/portal/billing"
            className="inline-flex items-center gap-1.5 text-[13px] text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-blue)] mb-6 transition-colors"
            data-testid="portal-academy-back-billing"
          >
            <ChevronLeft size={14} /> Gérer mon abonnement
          </Link>

          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-md border border-[color:var(--gv-line)] text-[color:var(--gv-blue)] px-3 py-1.5 rounded-full text-[10.5px] uppercase tracking-[0.15em] font-medium mb-6 shadow-sm">
            <Sparkles size={11} /> En lecture sur geoVINUM Academy
          </div>

          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.02] mb-5 max-w-3xl text-[color:var(--gv-ink)]"
            data-testid="portal-academy-title"
          >
            {featured?.title || "Votre bibliothèque geoVINUM Academy"}
          </h1>

          <p className="text-[color:var(--gv-ink)]/70 max-w-xl leading-relaxed mb-8 text-base md:text-lg">
            Explorez nos séries vidéo sur les terroirs, les cépages et la
            dégustation. Nouvelles masterclasses chaque mois.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            {featured && (
              <button
                onClick={() => play(featured)}
                className="btn-primary"
                data-testid="portal-academy-play-featured"
              >
                <Play size={14} fill="currentColor" /> Regarder maintenant
              </button>
            )}
            <button
              onClick={() =>
                document
                  .getElementById("library-grid")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" })
              }
              className="btn-ghost"
              data-testid="portal-academy-browse"
            >
              Parcourir la bibliothèque <ArrowRight size={14} />
            </button>
          </div>

          {/* Library stats */}
          <div className="mt-12 flex flex-wrap gap-x-10 gap-y-3 text-[13px] text-[color:var(--gv-ink)]/60 border-t border-[color:var(--gv-line)] pt-6">
            <div>
              <span className="text-[color:var(--gv-ink)] font-medium">
                {videos.length}
              </span>{" "}
              épisodes
            </div>
            <div className="hidden md:block">
              <span className="text-[color:var(--gv-ink)] font-medium">
                {sub?.status === "trialing" ? "Essai" : "Actif"}
              </span>{" "}
              · Abonnement {sub?.price_label || "Academy"}
            </div>
            <div className="hidden md:block">Nouveautés chaque mois</div>
          </div>
        </div>
      </section>

      {/* SEARCH BAR (local commandbar) */}
      <section className="bg-[#fafaf7] border-b border-[color:var(--gv-line)] sticky top-0 z-30 backdrop-blur-md bg-[#fafaf7]/85">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center gap-4">
          <div className="flex-1 max-w-md relative">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/40"
            />
            <input
              type="search"
              placeholder="Rechercher une masterclass, un terroir, un cépage…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-[14px] rounded-full bg-white border border-[color:var(--gv-line)] focus:border-[color:var(--gv-blue)] focus:outline-none focus:ring-4 focus:ring-[color:var(--gv-blue)]/10 transition-all"
              data-testid="portal-academy-search"
            />
          </div>
          <Link
            to="/portal/billing"
            className="hidden md:inline-flex items-center gap-1.5 text-[13px] text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-blue)] transition-colors"
          >
            <Tv size={13} /> Mon compte
          </Link>
        </div>
      </section>

      {/* CONTINUE WATCHING / NEW DROPS — horizontal rows */}
      {newDrops.length > 0 && (
        <HorizontalRow
          title="Nouveautés"
          subtitle="Récemment ajoutés à votre bibliothèque"
          items={newDrops}
          onPlay={play}
          variant="wide"
          testId="row-new"
        />
      )}

      {continueWatching.length > 0 && (
        <HorizontalRow
          title="À découvrir"
          subtitle="Sélection geoVINUM"
          items={continueWatching}
          onPlay={play}
          variant="poster"
          testId="row-continue"
        />
      )}

      {/* FULL LIBRARY GRID */}
      <section
        id="library-grid"
        className="bg-[#fafaf7] py-16 md:py-20 border-t border-[color:var(--gv-line)]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
            <div>
              <h2 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)] mb-1">
                Toute la bibliothèque
              </h2>
              <p className="text-[13px] text-[color:var(--gv-ink)]/60">
                {filtered.length} épisode{filtered.length > 1 ? "s" : ""} disponible
                {filtered.length > 1 ? "s" : ""}
                {query && ` — résultats pour "${query}"`}
              </p>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="py-20 text-center text-[color:var(--gv-ink)]/50">
              {query
                ? "Aucun résultat pour cette recherche."
                : "Aucun épisode disponible pour le moment — de nouveaux contenus arrivent bientôt."}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
              {filtered.map((v, idx) => (
                <PortalVideoCard
                  key={v.guid}
                  video={v}
                  index={idx}
                  onPlay={() => play(v)}
                  variant="grid"
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {playing && <Player video={playing} onClose={() => setPlaying(null)} />}
    </Layout>
  );
};

// ---------------- Horizontal row with scroll arrows ----------------
const HorizontalRow = ({ title, subtitle, items, onPlay, variant, testId }) => {
  const scrollerRef = useRef(null);
  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: "smooth" });
  };
  return (
    <section className="bg-[#fafaf7] pt-12 md:pt-14" data-testid={testId}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mb-5 flex items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl md:text-3xl text-[color:var(--gv-ink)]">
            {title}
          </h2>
          {subtitle && (
            <p className="text-[13px] text-[color:var(--gv-ink)]/60 mt-0.5">
              {subtitle}
            </p>
          )}
        </div>
        <div className="hidden md:flex items-center gap-1.5">
          <button
            onClick={() => scroll(-1)}
            className="w-9 h-9 rounded-full border border-[color:var(--gv-line)] bg-white hover:bg-[color:var(--gv-blue)] hover:text-white hover:border-[color:var(--gv-blue)] transition-all flex items-center justify-center text-[color:var(--gv-ink)]"
            aria-label="Scroll left"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={() => scroll(1)}
            className="w-9 h-9 rounded-full border border-[color:var(--gv-line)] bg-white hover:bg-[color:var(--gv-blue)] hover:text-white hover:border-[color:var(--gv-blue)] transition-all flex items-center justify-center text-[color:var(--gv-ink)]"
            aria-label="Scroll right"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
      <div
        ref={scrollerRef}
        className="overflow-x-auto scrollbar-hide pl-6 lg:pl-10"
      >
        <div
          className="flex gap-5 pb-2 pr-6 lg:pr-10"
          style={{ minWidth: "max-content" }}
        >
          {items.map((v, idx) => (
            <PortalVideoCard
              key={v.guid}
              video={v}
              index={idx}
              onPlay={() => onPlay(v)}
              variant={variant}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------------- Video card (3 variants) ----------------
const PortalVideoCard = ({ video, index, onPlay, variant = "grid" }) => {
  const isWide = variant === "wide";
  const isPoster = variant === "poster";
  const widthClass = isWide
    ? "w-[320px] md:w-[380px]"
    : isPoster
    ? "w-[200px] md:w-[230px]"
    : "w-full";
  const aspect = isWide ? "aspect-video" : isPoster ? "aspect-[2/3]" : "aspect-video";

  return (
    <button
      onClick={onPlay}
      className={`group block ${widthClass} shrink-0 text-left transition-transform duration-300 hover:-translate-y-1`}
      data-testid={`portal-video-${video.guid}`}
    >
      <div
        className={`${aspect} rounded-[8px] overflow-hidden bg-white relative border border-[color:var(--gv-line)] shadow-sm group-hover:shadow-xl group-hover:border-[color:var(--gv-blue)]/40 transition-all duration-300`}
      >
        {video.thumbnail ? (
          <img
            src={video.thumbnail}
            alt={video.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[color:var(--gv-ink)]/30 text-xs">
            Pas d&apos;image
          </div>
        )}

        {/* Hover veil */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-between p-4">
          <div className="text-white">
            <div className="text-[11px] uppercase tracking-widest opacity-80">
              Épisode {String(index + 1).padStart(2, "0")}
            </div>
            {video.length && (
              <div className="text-[12px] opacity-90 flex items-center gap-1 mt-0.5">
                <Clock size={11} /> {formatDuration(video.length)}
              </div>
            )}
          </div>
          <div className="bg-white text-[color:var(--gv-blue)] rounded-full p-3 shadow-lg">
            <Play size={16} fill="currentColor" />
          </div>
        </div>

        {/* Static episode badge */}
        <div className="absolute top-2.5 left-2.5 bg-white/90 backdrop-blur-sm text-[color:var(--gv-blue)] text-[10px] uppercase tracking-[0.15em] font-medium px-2 py-1 rounded-full border border-[color:var(--gv-line)]">
          E{String(index + 1).padStart(2, "0")}
        </div>
      </div>

      <div className="mt-3 text-[14px] font-medium text-[color:var(--gv-ink)] leading-snug line-clamp-2 group-hover:text-[color:var(--gv-blue)] transition-colors">
        {video.title}
      </div>
      {video.length ? (
        <div className="text-[11.5px] text-[color:var(--gv-ink)]/55 mt-0.5">
          {formatDuration(video.length)}
        </div>
      ) : null}
    </button>
  );
};

// ---------------- Player Modal ----------------
const Player = ({ video, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/95 z-50 flex flex-col"
      data-testid="player-modal"
    >
      <div className="flex items-center justify-between px-6 py-4 bg-black/40 backdrop-blur-md text-white">
        <div className="font-display text-base md:text-lg truncate pr-4">
          {video.title}
        </div>
        <button
          onClick={onClose}
          className="text-white/70 hover:text-white transition-colors rounded-full p-1.5 hover:bg-white/10"
          aria-label="Close player"
          data-testid="player-close"
        >
          <X size={20} />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-6xl aspect-video rounded-[8px] overflow-hidden shadow-2xl">
          <iframe
            src={video.iframe_url}
            loading="lazy"
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            className="w-full h-full border-0 bg-black"
            title={video.title}
            data-testid="player-iframe"
          />
        </div>
      </div>
    </div>
  );
};

export default PortalAcademyPage;
